import type { ReactElement } from 'react';
import { isValidElement, useEffect, useState } from 'react';
import {
  ModalContext as AriaModalContext,
  Provider,
} from 'react-aria-components';

import { ErrorBoundary } from '@infinex-private/ui-react-error-boundary';

import {
  ButtonContext,
  ButtonGroupContext,
  LinkButtonContext,
} from '../button';
import {
  ContentContext,
  FooterContext,
  HeaderContext,
  HeadingContext,
  ImageContext,
} from '../content';
import { InputContext } from '../field';
import { TextLink } from '../link';
import { ToastQueue } from '../toast';

// Component
// -----------------------------------------------------------------------------

/**
 * How long a dismissed dialog may remain mounted for its exit animation before
 * being forcibly removed. Comfortably beyond the longest exit animation used
 * by the dialog primitives (~400ms including delays).
 */
const EXIT_FALLBACK_MS = 1000;

export type DialogMarshalProps = {
  /** The dialog to display, conditionally. */
  children: ReactElement | false | null | undefined;
  /** Handler that is called when the dialog is dismissed. */
  onDismiss: () => void;
};

/**
 * A controller for dialog state, without the requirement for a trigger element.
 *
 * [Taurus docs: `DialogMarshal`](https://ui-sb.app.infinex.dev/?path=/docs/components-dialogmarshal--docs)
 */
export function DialogMarshal(props: DialogMarshalProps) {
  const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null);

  // Track children over time to manage the open state. Keep a reference of the
  // "last child" to use during exit animations.
  const child = isValidElement(props.children) ? props.children : null;
  const [lastChild, setLastChild] = useState<ReactElement | null>(child);
  if (child && child !== lastChild) {
    setLastChild(child);
  }

  const modalContext = {
    isOpen: !!child,
    onOpenChange: (isOpen: boolean) => !isOpen && props.onDismiss(),
    ref: setModalRef,
  };

  // React-aria keeps a dismissed dialog mounted until its exit animations
  // report completion, but that report never arrives when an animation is
  // cancelled or runs indefinitely — leaving a dead, unresponsive dialog on
  // screen. Force-release the retained dialog if the exit hasn't completed
  // within the fallback window.
  useEffect(() => {
    if (child || !modalRef) return;
    const timer = setTimeout(() => setModalRef(null), EXIT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [child, modalRef]);

  /*
    Derived state used to ensure dialogs mount/unmount:
    - `child` is available while entering
    - `modalRef` is available while exiting
  */
  const dialogElement = Boolean(child || modalRef) && lastChild;

  return (
    <ErrorBoundary
      name="dialog"
      onError={() => {
        ToastQueue.add('error', {
          title: 'Something went wrong',
          content: (
            <>
              If the problem persists, please{' '}
              <TextLink isExternal href="https://support.infinex.xyz">
                contact support
              </TextLink>
            </>
          ),
        });
        props.onDismiss();
      }}
    >
      <Provider
        // clear contexts, so children can compose slot components
        values={[
          [ButtonContext, null],
          [ButtonGroupContext, null],
          [LinkButtonContext, null],
          [ContentContext, null],
          [FooterContext, null],
          [HeaderContext, null],
          [HeadingContext, null],
          [ImageContext, null],
          [InputContext, null],
        ]}
      >
        <AriaModalContext.Provider value={modalContext}>
          {dialogElement}
        </AriaModalContext.Provider>
      </Provider>
    </ErrorBoundary>
  );
}
